import React, { useEffect } from 'react';
import { ReceiptTotalTitle, ReceiptLineOccupancy, ReceiptLineSummary, ReceiptLineAddNight } from '../../components';
import './PaymentConfirmation.scss';
import { CurrencyUtils, TranslationUtils } from '../../utils';
import { useIntl } from 'react-intl';

import { useHistory, useLocation } from 'react-router-dom';
import { AppRoutes } from '../../routes';
import { OccupancyCounts, OccupancyKeys } from '../../types/occupancy';
import { AdjustmentPolicies, TripAdjustmentResponse } from 'guest-data-types-ts-lib';
import { ReceiptLineExtendStay } from '../../components/Receipt/ReceiptLineExtendStay';
import { ReceiptLinePetAdjustment } from '../../components/Receipt/ReceiptLinePetAdjustment';
import { DiscountDetail } from '../../types/receipt.type';
import { ReceiptLineLateCheckOutAdjustment } from '../../components/Receipt/ReceiptLineLateCheckOutAdjustment';
import _ from 'lodash';

export interface PaymentConfirmationAdjustmentProps {
    adjustment: AdjustmentPolicies;
    preview: TripAdjustmentResponse;
    discount_details?: DiscountDetail;
    occupancy_details?: { initialOccupancy: OccupancyCounts | null; occupancyCounts: OccupancyCounts | null };
}

export const PaymentConfirmationAdjustment: React.FC = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const intl = useIntl();
    const history = useHistory();
    const location = useLocation<PaymentConfirmationAdjustmentProps>();

    if (!location || !location.state || !location.state.preview || !location.state.adjustment) {
        history.push(AppRoutes.RESERVATION);
        return null;
    }

    const { adjustment, preview, discount_details, occupancy_details } = location.state;
    const { delta, converted_delta, detail, adjustment: adjustmentData } = preview;
    const isOnlyAdjustmentFlow = adjustment === 'updatePetAdjustment' && detail && detail?.previous_balance > detail?.total;
    const isOccupancyAdjustment = adjustment === 'adjustOccupancyAndPets' || adjustment === 'adjustOccupancy';
    const shouldShowPreviousBalance = (isOccupancyAdjustment || adjustment === 'updatePetAdjustment') && detail!.previous_balance > 0;

    const isAddNightFlow = adjustment === 'addNight' || adjustment === 'tripAddNight';

    const getAdjustmentUpdatePetTextPaymentConfirmation = (): string => {
        if (_.isNil(detail?.refund)) {
            const petText = TranslationUtils.get(
                `${
                    (
                        adjustmentData as {
                            name: 'updatePetAdjustment';
                            data: {
                                pets: number;
                            };
                        }
                    ).data.pets === 1
                        ? 'pet'
                        : 'pets'
                }`
            );
            return TranslationUtils.get('payment_successful_add_pet_adjustment_note', {
                total: (
                    adjustmentData as {
                        name: 'updatePetAdjustment';
                        data: {
                            pets: number;
                        };
                    }
                ).data.pets,
                pet: petText.toLocaleLowerCase(),
            });
        }
        const petDiff =
            occupancy_details!.initialOccupancy![OccupancyKeys.PETS].count - occupancy_details!.occupancyCounts![OccupancyKeys.PETS].count;
        const petText = TranslationUtils.get(
            `${
                occupancy_details!.initialOccupancy![OccupancyKeys.PETS].count -
                    (
                        adjustmentData as {
                            name: 'updatePetAdjustment';
                            data: {
                                pets: number;
                            };
                        }
                    ).data.pets ===
                1
                    ? 'pet'
                    : 'pets'
            }`
        );
        return TranslationUtils.get('payment_successful_add_pet_adjustment_refund_note', {
            total: petDiff,
            pet: petText.toLocaleLowerCase(),
        });
    };

    return (
        <React.Fragment>
            <div className="payment-confirmation-header">
                <div tabIndex={0}>
                    <h1 className="page-title mb-0">
                        {adjustment === 'extendedStay' || adjustment === 'updatePetAdjustment'
                            ? TranslationUtils.get('receipt_title')
                            : TranslationUtils.get('payment_successful')}
                    </h1>
                </div>
                <p tabIndex={0} className="mt-3">
                    {isAddNightFlow && TranslationUtils.get('payment_successful_note')}
                    {adjustment === 'updatePetAdjustment' &&
                        !_.isNil(occupancy_details) &&
                        !_.isNil(occupancy_details.initialOccupancy) &&
                        getAdjustmentUpdatePetTextPaymentConfirmation()}
                    {(isOccupancyAdjustment || adjustment === 'lateCheckout') && TranslationUtils.get('payment_successful_generic_note')}

                    {adjustment === 'poolHeat' && TranslationUtils.get('payment_successful_add_pool_heat_adjustment_note')}
                </p>
            </div>
            {isAddNightFlow && (
                <ReceiptTotalTitle
                    summary={{
                        total: delta.total,
                        currency: delta.currency,
                    }}
                    convertedSummary={converted_delta}
                    locale={intl.locale}
                />
            )}
            {isOccupancyAdjustment && (
                <ReceiptTotalTitle
                    summary={{
                        total: detail!.total,
                        currency: detail!.currency,
                    }}
                    convertedSummary={
                        detail!.converted
                            ? {
                                  total: detail!.converted.total,
                                  currency: detail!.converted.currency,
                              }
                            : undefined
                    }
                    locale={intl.locale}
                />
            )}
            {adjustment === 'extendedStay' ||
                (adjustment === 'lateCheckout' && (
                    <ReceiptTotalTitle
                        summary={{
                            total: delta.total,
                            currency: delta.currency,
                        }}
                        locale={intl.locale}
                    />
                ))}
            {adjustment === 'updatePetAdjustment' && (
                <ReceiptTotalTitle
                    summary={{
                        total: isOnlyAdjustmentFlow ? detail!.total : delta.total,
                        currency: delta.currency,
                    }}
                    locale={intl.locale}
                    text={isOnlyAdjustmentFlow ? TranslationUtils.get('new_balance_due', { currency: delta.currency }) : undefined}
                />
            )}
            {adjustment === 'poolHeat' && (
                <ReceiptTotalTitle
                    summary={{
                        total: isOnlyAdjustmentFlow ? detail!.total : delta.total,
                        currency: delta.currency,
                    }}
                    locale={intl.locale}
                    text={isOnlyAdjustmentFlow ? TranslationUtils.get('new_balance_due', { currency: delta.currency }) : undefined}
                />
            )}

            <div className="payment-confirmation-content">
                {isAddNightFlow && <ReceiptLineAddNight preview={preview} discount_details={discount_details} locale={intl.locale} />}
                {isOccupancyAdjustment && (
                    <ReceiptLineOccupancy occupancy_details={occupancy_details} preview={preview} locale={intl.locale} />
                )}
                {adjustment === 'extendedStay' && <ReceiptLineExtendStay preview={preview} locale={intl.locale} />}
                {adjustment === 'lateCheckout' && <ReceiptLineLateCheckOutAdjustment preview={preview} locale={intl.locale} />}
                {adjustment === 'updatePetAdjustment' && (
                    <ReceiptLinePetAdjustment occupancy_details={occupancy_details} preview={preview} locale={intl.locale} />
                )}
                <ReceiptLineSummary
                    summaryTitle={TranslationUtils.get('fees')}
                    summaryValue={CurrencyUtils.formatCurrency(delta?.fees || 0, delta.currency)}
                    currency={delta.currency}
                    locale={intl.locale}
                />
                <ReceiptLineSummary
                    summaryTitle={TranslationUtils.get('taxes')}
                    summaryValue={CurrencyUtils.formatCurrency(delta?.taxes || 0, delta.currency)}
                    currency={delta.currency}
                    locale={intl.locale}
                />
                {shouldShowPreviousBalance && (
                    <>
                        <hr />
                        {
                            <ReceiptLineSummary
                                summaryTitle={TranslationUtils.get('previous_balance')}
                                summaryValue={CurrencyUtils.formatCurrency(detail!.previous_balance, delta.currency)}
                                currency={delta.currency}
                                locale={intl.locale}
                            />
                        }
                        <hr />
                    </>
                )}
                {adjustment === 'extendedStay' && (
                    <>
                        <hr />
                        {
                            <ReceiptLineSummary
                                summaryTitle={TranslationUtils.get('balance_in', { currency: delta.currency })}
                                summaryValue={CurrencyUtils.formatCurrency(0, delta.currency)}
                                currency={delta.currency}
                                locale={intl.locale}
                            />
                        }
                    </>
                )}
            </div>
        </React.Fragment>
    );
};
